/* Unpublished cart experiment. One code; confirmed server state; no analytics or request replay. */
(() => {
  'use strict';
  if (customElements.get('igneous-cart-discounts')) return;
  const state = {owner: null, recovery: false, finishing: false, epoch: 0, instances: new Set(), controls: new Map(), inert: new Map(), busy: new Map()};
  const blocked = () => Boolean(state.owner || state.recovery || state.finishing);
  const scopes = () => [...new Set([...state.instances].map(x => x.scope).concat([...state.busy.keys()], [...document.querySelectorAll('[data-section-type="cart"], [data-section-type="cart-drawer"]')]).filter(x => x?.isConnected))];
  const allowed = target => target?.closest?.('[data-drawer-toggle], [data-drawer-underlay], [data-discount-reload]');
  function lock() {
    scopes().forEach(scope => {
      if (!state.busy.has(scope)) state.busy.set(scope, scope.getAttribute('aria-busy'));
      scope.setAttribute('aria-busy', state.recovery ? 'false' : 'true');
      scope.querySelectorAll('button,input,select,textarea').forEach(element => {
        if (allowed(element)) return;
        if (!state.controls.has(element)) state.controls.set(element, element.disabled);
        element.disabled = true;
      });
    });
    document.querySelectorAll('igneous-cart-experiment').forEach(element => {
      if (!state.inert.has(element)) state.inert.set(element, element.inert);
      element.inert = true;
    });
    state.instances.forEach(instance => instance.setAttribute('aria-busy', state.recovery ? 'false' : 'true'));
  }
  function unlock() {
    state.controls.forEach((disabled, element) => { element.disabled = disabled; }); state.controls.clear();
    state.inert.forEach((inert, element) => { element.inert = inert; }); state.inert.clear();
    state.busy.forEach((busy, element) => { if (busy === null) element.removeAttribute('aria-busy'); else element.setAttribute('aria-busy', busy); }); state.busy.clear();
    state.instances.forEach(instance => { instance.setAttribute('aria-busy', 'false'); instance.enable(); if (!instance.ready) instance.sync(); });
  }
  ['click', 'submit', 'change', 'input'].forEach(type => document.addEventListener(type, event => {
    if (!blocked() || allowed(event.target)) return;
    if (scopes().some(scope => scope.contains(event.target)) || event.target?.closest?.('igneous-cart-experiment')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true));
  // Replaced native line-item controls must inherit the pending/recovery fence.
  new MutationObserver(() => { if (blocked()) lock(); }).observe(document.documentElement, {childList: true, subtree: true});
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
  document.addEventListener('theme:cart:change', event => {
    state.epoch++;
    if (state.owner) { if (!state.finishing) state.owner.externalChange = true; return; }
    if (state.recovery || state.finishing) return;
    state.instances.forEach(instance => instance.accept(event.detail?.cart, true));
  });
  class CartDiscounts extends HTMLElement {
    async connectedCallback() {
      if (this.session?.active) return;
      const session = this.session = {active: true, abort: new AbortController()};
      const options = {signal: session.abort.signal};
      this.field = this.querySelector('[data-discount-input]');
      this.apply = this.querySelector('[data-discount-apply]');
      this.removeButton = this.querySelector('[data-discount-remove]');
      this.reload = this.querySelector('[data-discount-reload]');
      this.status = this.querySelector('[data-discount-status]');
      this.error = this.querySelector('[data-discount-error]');
      this.form = this.closest('form.cart');
      this.scope = this.closest('[data-section-type="cart"], [data-section-type="cart-drawer"]') || this.form;
      try { this.messages = JSON.parse(this.querySelector('[data-discount-text]').textContent); } catch (_) { this.messages = {}; }
      if (!this.form || !this.scope || !this.field || !this.apply || !this.removeButton || !this.reload || !this.status || !this.error) return;
      const cart = new URL(this.dataset.cartUrl, location.origin);
      if (cart.origin !== location.origin) return;
      this.readUrl = cart.pathname.replace(/\/$/, '') + '.js';
      this.updateUrl = cart.pathname.replace(/\/$/, '') + '/update.js';
      this.ready = false; this.dirty = false; this.lastCart = null;
      state.instances.add(this);
      this.apply.addEventListener('click', () => this.update(false), options);
      this.removeButton.addEventListener('click', () => this.update(true), options);
      this.reload.addEventListener('click', () => location.reload(), options);
      this.field.addEventListener('input', () => {
        this.dirty = true; this.flash = null; this.error.textContent = ''; this.status.textContent = '';
        this.field.removeAttribute('aria-invalid'); this.updateClear();
      }, options);
      this.field.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.update(false); }
      }, options);
      this.scope.addEventListener('theme:drawer:open', () => this.sync(), {...options, capture: true});
      if (blocked()) { lock(); if (state.recovery) this.showRecovery(); else this.status.textContent = this.text('pending'); return; }
      await this.sync();
    }
    disconnectedCallback() {
      if (!this.session?.active) return;
      this.session.active = false; this.session.abort.abort(); state.instances.delete(this);
      if (state.owner === this) {
        state.owner = null;
        if (this.writeStarted) { state.recovery = true; state.instances.forEach(x => x.showRecovery()); lock(); }
        else unlock();
      }
    }
    active(session) { return this.session === session && session.active && this.isConnected; }
    assertActive(session) { if (!this.active(session) || this.externalChange) throw Error('Cart changed during discount update'); }
    text(key) { return this.messages[key] || ''; }
    enable() {
      this.field.disabled = this.apply.disabled = this.removeButton.disabled = !this.ready || blocked();
      this.updateClear();
    }
    updateClear() { this.removeButton.hidden = !this.field.value && !this.lastCart?.discount_codes?.length; }
    codes(cart, applicableOnly = false) {
      if (!cart || !Array.isArray(cart.discount_codes) || cart.discount_codes.some(x => !x || typeof x.code !== 'string' || !x.code.trim() || typeof x.applicable !== 'boolean')) throw Error('Invalid discount metadata');
      const codes = cart.discount_codes.filter(x => !applicableOnly || x.applicable).map(x => x.code.trim().toUpperCase());
      if (new Set(codes).size !== codes.length) throw Error('Duplicate discount metadata');
      return codes;
    }
    validateCart(cart) {
      this.codes(cart);
      if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || typeof cart.token !== 'string' || !cart.token || typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) throw Error('Incomplete cart response');
      for (const field of ['total_price', 'total_discount', 'items_subtotal_price']) if (!Number.isSafeInteger(cart[field]) || cart[field] < 0) throw Error('Invalid totals');
      let quantity = 0;
      for (const item of cart.items) {
        if (!Number.isSafeInteger(Number(item?.variant_id || item?.id)) || Number(item.variant_id || item.id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) throw Error('Invalid item');
        quantity += item.quantity;
      }
      if (quantity !== cart.item_count) throw Error('Incomplete cart');
      return cart;
    }
    async request(url, options = {}) {
      const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 15000);
      const signal = this.session.abort.signal; const cancel = () => controller.abort();
      if (signal.aborted) controller.abort(); else signal.addEventListener('abort', cancel, {once: true});
      try {
        const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal});
        if (!response.ok) throw Error('Cart request failed');
        return this.validateCart(await response.json());
      } finally { clearTimeout(timer); signal.removeEventListener('abort', cancel); }
    }
    read() { return this.request(this.readUrl, {headers: {Accept: 'application/json'}}); }
    write(code) { return this.request(this.updateUrl, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({discount: code})}); }
    basketFingerprint(cart) {
      return JSON.stringify({token: cart.token, currency: cart.currency, note: cart.note, attributes: cart.attributes, items: cart.items.map(x => ({variant: x.variant_id || x.id, quantity: x.quantity, properties: x.properties, sellingPlan: x.selling_plan_allocation?.selling_plan?.id}))});
    }
    resultFingerprint(cart) {
      return JSON.stringify({basket: this.basketFingerprint(cart), codes: cart.discount_codes.map(x => [x.code.toUpperCase(), x.applicable]), total: cart.total_price, discount: cart.total_discount, itemsSubtotal: cart.items_subtotal_price});
    }
    noticeFingerprint(cart) {
      // Brief UI feedback only. No token, customer data or tracking.
      return JSON.stringify({codes: cart.discount_codes.map(x => [x.code.toUpperCase(), x.applicable]), total: cart.total_price, discount: cart.total_discount, items: cart.items.map(x => [x.variant_id || x.id, x.quantity])});
    }
    accept(cart, preserveDraft = false) {
      if (!this.session?.active || blocked()) return;
      try {
        this.validateCart(cart); this.lastCart = cart; this.ready = true;
        const codes = this.codes(cart, true);
        if (!preserveDraft || !this.dirty) { this.field.value = codes.length === 1 ? codes[0] : ''; this.field.removeAttribute('aria-invalid'); }
        this.status.textContent = codes.length > 1 ? this.text('multipleExisting') : '';
        this.error.textContent = ''; this.reload.hidden = true; this.setAttribute('aria-busy', 'false'); this.enable();
        if (!this.flashRead) {
          this.flashRead = true;
          try { this.flash = JSON.parse(sessionStorage.getItem('igneous-cart-discount-result')); sessionStorage.removeItem('igneous-cart-discount-result'); } catch (_) {}
        }
        if (this.flash?.fingerprint === this.noticeFingerprint(cart)) {
          if (this.flash.result.startsWith('invalid')) this.error.textContent = this.text(this.flash.result);
          else this.status.textContent = this.text(this.flash.result);
        } else this.flash = null;
      } catch (_) { this.unavailable(); }
    }
    async sync() {
      if (!this.session?.active || blocked()) return;
      const session = this.session, epoch = state.epoch, revision = this.readRevision = (this.readRevision || 0) + 1;
      try {
        const cart = await this.read();
        if (!this.active(session) || blocked() || epoch !== state.epoch || revision !== this.readRevision) return;
        this.accept(cart, true);
      } catch (_) { if (this.active(session) && !blocked() && epoch === state.epoch && revision === this.readRevision) this.unavailable(); }
    }
    unavailable() {
      this.ready = false; this.enable(); this.status.textContent = ''; this.error.textContent = this.text('loadFailed');
      this.reload.hidden = false; this.setAttribute('aria-busy', 'false');
    }
    showRecovery() {
      this.ready = false; this.status.textContent = ''; this.error.textContent = this.text('unknown');
      this.reload.hidden = false; this.setAttribute('aria-busy', 'false');
    }
    finish(result, cart, before, rate) {
      state.finishing = true;
      // Reconcile both visible widgets to the independently confirmed result before reloading totals.
      state.instances.forEach(instance => {
        instance.lastCart = cart; instance.field.value = instance.codes(cart, true).length === 1 ? instance.codes(cart, true)[0] : '';
        instance.dirty = false; instance.updateClear();
        instance.status.textContent = result.startsWith('invalid') ? '' : instance.text(result);
        instance.error.textContent = result.startsWith('invalid') ? instance.text(result) : '';
      });
      try { sessionStorage.setItem('igneous-cart-discount-result', JSON.stringify({result, fingerprint: this.noticeFingerprint(cart)})); } catch (_) {}
      document.dispatchEvent(new CustomEvent('theme:cart:change', {detail: {cart}, bubbles: true}));
      const delay = result === 'applied' || result === 'removed' ? window.IgneousCartFeedback?.confirm({before, current: cart, source: 'discount', rate}) || 0 : 0;
      if (delay) setTimeout(() => location.reload(), delay); else location.reload();
    }
    async update(clear) {
      if (!this.ready || blocked() || !this.session?.active) return;
      const draft = this.field.value.trim();
      if (!clear && (!draft || draft.length > 255 || /[\s,;\u0000-\u001f\u007f]/.test(draft))) {
        this.error.textContent = this.text(draft ? 'single' : 'empty'); this.field.setAttribute('aria-invalid', 'true'); this.field.focus(); return;
      }
      if (document.querySelector('[data-cart-loading].cart--loading, igneous-cart-experiment[aria-busy="true"]')) { this.error.textContent = this.text('wait'); return; }
      const requested = clear ? '' : draft.toUpperCase();
      if (clear && this.lastCart && this.codes(this.lastCart).length === 0) {
        this.field.value = ''; this.dirty = false; this.flash = null; this.field.removeAttribute('aria-invalid'); this.error.textContent = ''; this.status.textContent = ''; this.updateClear(); this.field.focus(); return;
      }
      const session = this.session; this.externalChange = false; this.writeStarted = false; state.owner = this;
      state.instances.forEach(x => { x.flash = null; x.status.textContent = x.text('pending'); x.error.textContent = ''; x.field.removeAttribute('aria-invalid'); }); lock();
      let shippingWrite, shippingAfter;
      try { shippingWrite = window.IgneousCartFeedback?.beginWrite?.(); } catch (_) {}
      try {
        const before = await this.read(); this.assertActive(session);
        const rate = Number(window.Shopify?.currency?.rate), basket = this.basketFingerprint(before);
        this.writeStarted = true;
        const acknowledged = await this.write(requested); this.assertActive(session);
        const current = await this.read(); this.assertActive(session);
        if (basket !== this.basketFingerprint(acknowledged) || basket !== this.basketFingerprint(current) || this.resultFingerprint(acknowledged) !== this.resultFingerprint(current)) throw Error('Uncertain cart result');
        shippingAfter = current;
        const codes = this.codes(current), applied = this.codes(current, true);
        if (requested ? codes.length === 1 && codes[0] === requested && applied[0] === requested : codes.length === 0) {
          this.finish(clear ? 'removed' : 'applied', current, before, rate); return;
        }
        if (clear) throw Error('Removal unconfirmed');
        // One acknowledged write and one independent read establish server truth. Never restore with a second POST.
        this.finish(applied.length > 1 ? 'invalidMultiple' : applied.length ? 'invalidRetained' : 'invalidEmpty', current, before, rate);
      } catch (_) {
        if (!this.active(session)) return;
        state.owner = null;
        if (this.writeStarted) {
          state.recovery = true; state.instances.forEach(x => x.showRecovery()); lock();
          if (this.getClientRects().length && !this.closest('[aria-hidden="true"]')) this.reload.focus();
        } else {
          unlock(); state.instances.forEach(x => { x.status.textContent = ''; }); this.error.textContent = this.text('readFailed');
        }
      } finally {
        try { window.IgneousCartFeedback?.endWrite?.(shippingWrite, shippingAfter); } catch (_) {}
      }
    }
  }
  customElements.define('igneous-cart-discounts', CartDiscounts);
})();
